export default () => ({
    api: { entries: 0, pages: 1, currentPage: 1, perPage: 0, nextPage: null },
    endpoints: [
        { name: 'resources', url: '/resources.json', title: 'All' },
        { name: 'blog', url: '/blog.json', title: 'Blog' },
        { name: 'arc-labs', url: '/arclabs.json', title: 'ARC Labs' },
        // { name: 'knowledgebase', url: '/knowledgebase.json', title: 'Learning Center'},
    ],
    categories: [
        { name: 'case-studies', url: '/casestudies.json' },
        { name: 'data-sheets', url: '/datasheets.json' },
        { name: 'infographics', url: '/datasheets.json' },
        { name: 'podcasts', url: '/podcasts.json' },
        { name: 'webinars', url: '/webinars.json' },
        { name: 'whitepapers', url: '/whitepapers.json' },
        { name: 'videos', url: '/videos.json' },
    ],
    activeEndpoint: null,
    isReady: false,
    isLoadingMore: false,
    paginateLimit: 9,
    currentShown: 9, // should match paginateLimit on init
    currentFilters: {},
    items: [],
    filteredItems: [],

    // bindings
    all: {
        [':class']: '!activeEndpoint ? "--is-active" : ""',
    },

    category: {
        [':class']: 'activeEndpoint == endpoint.name ? "--is-active" : ""',
    },

    // functions
    async init() {
        let current = document.querySelector('[data-current]')?.getAttribute('data-current') || null
        console.log('The current endpoint: ' + current)

        if (current) {
            // match to an endpoint
            let available = [...this.endpoints, ...this.categories].some(item => item.name === current)
            if (available) {
                await this.fetchEndpoint(current)
                return
            }
        }

        // default for null
        await this.fetchEndpoint('resources')
    },

    // grab a single endpoint from the array
    async fetchEndpoint(name) {
        this.activeEndpoint = name
        this.isReady = false

        // update the URL without reloading the page
        history.pushState(null, '', name === 'resources' ? '/resources' : `/resources/${name}`)

        let match = [...this.endpoints, ...this.categories].find(item => item.name === name)

        let json = null

        try {
            let response = await fetch(match.url)
            if (!response.ok) throw new Error(`HTTP ${response.status}`)

            json = await response.json()
            this.items = json.data || []
            this.filteredItems = this.items
            this.getPagination(json?.meta?.pagination || {})
        } catch (err) {
            console.error('Failed to fetch endpoint:', err)
            this.items = []
            this.filteredItems = []
            this.getPagination({})
        }

        // reset pagination on fresh data
        this.currentShown = this.paginateLimit

        // defer listener for alpine render
        requestAnimationFrame(() => {
            this.filterListeners()
            this.animateCards(0)
            this.isReady = true
        })
    },

    // load the next set of paginated data
    async loadMore() {
        if (this.isLoadingMore) return
        this.isLoadingMore = true

        let currentEntries = this.currentShown
        let remainingEntries = this.currentShown < this.filteredItems.length && !this.api.nextPage
        let moreData = this.currentShown + this.paginateLimit <= this.filteredItems.length
        let morePages = !!this.api.nextPage

        switch (true) {
            // show more from what we already have
            case moreData:
                this.currentShown = Math.min(this.currentShown + this.paginateLimit, this.filteredItems.length)
                break

            // show remaining even if less than limit
            case remainingEntries:
                this.currentShown = this.filteredItems.length
                break

            // fetch the next page from Element API, then show more
            case morePages:
                let response = await fetch(this.api.nextPage)
                let json = await response.json()

                this.getPagination(json?.meta?.pagination || {})
                this.appendData(json.data || [], currentEntries, true)
                break

            default:
                break
        }

        this.applyFilters({ preserveShown: true, animate: true })

        requestAnimationFrame(() => {
            this.animateCards(currentEntries)
            this.isLoadingMore = false
        })
    },

    // generate list for inputs
    mappedField(field) {
        let unique = new Set()

        this.items.forEach((item) => {
            ;(Array.isArray(item[field]) ? item[field] : [item[field]])
                .filter(Boolean)
                .forEach((entry) => unique.add(entry))
        })

        // alphabetical sort
        return Array.from(unique).sort((a, b) =>
            a.toLowerCase().localeCompare(b.toLowerCase())
        )
    },

    // Input event tracking
    filterListeners() {
        const filters = document.querySelector('[data-filters]')

        if (filters) {
            const mappedElements = filters.querySelectorAll('[data-map]')

            mappedElements.forEach((element) => {
                const inputs = element.querySelectorAll('input, select, textarea')

                inputs.forEach((input) => {
                    input.addEventListener('input', this.updateFilters.bind(this))
                    input.addEventListener('change', this.updateFilters.bind(this))
                })
            })
        }
    },

    // update filters based on changes
    updateFilters(event) {
        const input = event.target
        const getMap = input.closest('[data-map]')

        if (!getMap) return

        const mapField = getMap.getAttribute('data-map')
        const isArray = getMap.hasAttribute('data-array')

        let current = this.currentFilters[mapField] || []

        switch (input.type) {
            case 'checkbox':
                // Handle checkbox inputs
                this.currentFilters[mapField] = input.checked
                    ? [...current, input.value]
                    : current.filter(val => val !== input.value)

                if (!this.currentFilters[mapField].length) {
                    delete this.currentFilters[mapField]
                }
                break

            case 'range':
                // Handle range inputs
                this.currentFilters[mapField] = { value: input.value, type: 'range' }
                break

            default:
                // Handle text or dropdown inputs
                this.currentFilters[mapField] = isArray
                    ? { value: input.value, type: 'array' }
                    : input.value
        }

        this.applyFilters({ preserveShown: true, animate: true })
    },

    // check matches
    matchFilter(itemValue, filter) {
        if (!filter || String(filter).trim() === '') return true

        const normalise = String(filter).trim().toLowerCase()

        if (Array.isArray(itemValue)) {
            return itemValue.some(val =>
                String(val).trim().toLowerCase().includes(normalise)
            )
        }

        return String(itemValue).trim().toLowerCase().includes(normalise)
    },

    // apply active filters
    applyFilters({ preserveShown = false, animate = true } = {}) {
        const hasFilters = Object.keys(this.currentFilters).length > 0

        this.filteredItems = hasFilters
            ? this.items.filter(item =>
                Object.entries(this.currentFilters).every(([key, filter]) => {
                    switch (true) {
                        case Array.isArray(filter):
                            return filter.some(val => this.matchFilter(item[key], val))

                        case filter?.type === 'range':
                            // Range input filter
                            return item[key] >= filter.value

                        case filter?.type === 'array':
                            // Text input filters for arrays
                            return this.matchFilter(item[key], filter.value)

                        default:
                            // Text or dropdown input for strings/numbers
                            return this.matchFilter(item[key], filter)
                    }
                })
            )
            : this.items

        // reset pagination on changes
        if (!preserveShown) {
            this.currentShown = this.paginateLimit
        } else {
            this.currentShown = Math.min(
                Math.max(this.currentShown, this.paginateLimit),
                this.filteredItems.length
            )
        }

        if (animate) {
            requestAnimationFrame(() => this.animateCards(0))
        }
    },

    // Clear all filters
    clearFilters() {
        this.currentFilters = {}

        // Reset inputs
        const filters = document.querySelector('[data-filters]')

        if (filters) {
            filters.querySelectorAll('input, select, textarea').forEach(input => {
                if (['checkbox', 'radio'].includes(input.type)) {
                    input.checked = false

                } else if (input.type === 'range') {
                    input.value = input.min

                } else {
                    input.value = ''
                }
            })
        }

        this.applyFilters({ preserveShown: false, animate: true })
    },

    // helpers
    getPagination(data) {
        data = data ?? {}

        this.api.entries = data.total || 0
        this.api.pages = data.total_pages || 1
        this.api.currentPage = data.current_page || 1
        this.api.perPage = data.per_page || 0
        this.api.nextPage = data.links?.next || null

        if (this.api.currentPage >= this.api.pages) {
            this.api.nextPage = null
        }
    },

    // data stuff
    appendData(data, currentEntries, preserveShown = false) {
        this.items = [...this.items, ...data]
        this.applyFilters({ preserveShown, animate: false })
        this.currentShown = Math.min(currentEntries + this.paginateLimit, this.filteredItems.length)

        requestAnimationFrame(() => this.filterListeners())
    },

    // check if theres results
    hasResults() {
        return this.filteredItems.length > 0
    },

    // currently visible items
    currentItems() {
        return this.filteredItems.slice(0, this.currentShown)
    },

    // show/hide load more button
    showLoadMore() {
        return this.currentShown < this.filteredItems.length || this.api.nextPage
    },

    // animation stuff
    animateCards(start = 0) {
        const cards = document.querySelectorAll('.cards > *')

        cards.forEach((card, index) => {
            // skip already loaded or already animated
            if (index < start || card.dataset.animated === 'true') return

            // reflow
            void card.offsetHeight
            setTimeout(() => card.dataset.animated = 'true', (index - start) * 80)
        })
    },
})
